import { Component, ElementRef, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerComponent as KendoDatePickerComponent } from '@progress/kendo-angular-dateinputs';

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Shared date-picker input, styled to match PlusDoctor-v2's `app-date-picker`
 * (shared/components/date-picker) — a Kendo date picker with the same bordered/
 * rounded look instead of Angular Material's underline style.
 *
 * Implements ControlValueAccessor so it's a drop-in replacement for the old
 * `<input matInput [matDatepicker]>` markup wherever it's bound via
 * `[(ngModel)]` or `formControlName` — no change to the surrounding
 * component logic (validators, form group shape, change handlers) is needed.
 */
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.css'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() format = 'dd/MM/yyyy';
  @Input() readOnlyInput = false;

  /** Accepts a Date, an ISO/date-like string, or null/undefined — callers pass
   *  either shape across this app. Normalized to a real Date (or null) before
   *  ever reaching Kendo, since Kendo's own setter assumes a real Date. */
  @Input() set min(value: Date | string | null | undefined) {
    this._min = toDate(value);
  }
  @Input() set max(value: Date | string | null | undefined) {
    this._max = toDate(value);
  }

  private _min: Date | null = null;
  private _max: Date | null = null;

  value: Date | null = null;
  disabled = false;

  private readonly el = inject(ElementRef);

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Accepts a Date, an ISO/date-like string, or null/undefined — callers across
   *  this app store the bound property as either a `Date` (most reactive forms)
   *  or a plain date string (e.g. Login Log's `startDate: string`), matching
   *  what the old `<input matInput [matDatepicker]>` + Moment date adapter
   *  accepted. Kendo's own datepicker only understands real `Date` objects, so
   *  any string is parsed here before being handed to it. */
  writeValue(value: Date | string | null | undefined): void {
    this.value = toDate(value);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(date: Date | null): void {
    this.value = toDate(date);
    this.onChange(this.value);
    this.onTouched();
  }

  onClose(): void {
    setTimeout(() => {
      (this.el.nativeElement.querySelector('input.k-input-inner') as HTMLInputElement | null)?.blur();
    }, 0);
  }

  /** Returns a disabledDates predicate instead of binding Kendo's own `min`/`max`
   *  inputs directly — always a real function, so Kendo's internal checks never
   *  see anything but a Date (or nothing at all) regardless of what shape a
   *  caller's `min`/`max` binding produced. */
  disabledDatesFn(): (date: Date) => boolean {
    const min = this._min;
    const max = this._max;
    if (!min && !max) return () => false;

    const minMidnight = min ? new Date(min.getFullYear(), min.getMonth(), min.getDate()) : null;
    const maxMidnight = max ? new Date(max.getFullYear(), max.getMonth(), max.getDate()) : null;

    return (date: Date) => {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (minMidnight && d < minMidnight) return true;
      if (maxMidnight && d > maxMidnight) return true;
      return false;
    };
  }

  /** Opens the calendar on a click anywhere in the text box, not just the icon —
   *  same convention as PlusDoctor-v2's app-date-picker. */
  onHostClick(event: Event, picker: KendoDatePickerComponent): void {
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;
    picker.toggle(true);
  }
}
